import React, { useState } from 'react';
import { supabase } from '../supabase';
import { colors as theme } from '../theme';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

export default function SupportModal({ userEmail, onClose }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!subject.trim()) { setError('Please enter a subject.'); return; }
    if (!message.trim()) { setError('Please describe your issue.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-support-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ subject, message, app: 'Cipher Fitness' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Failed to send');
      setSent(true);
    } catch (err) {
      setError(err.message ?? 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        background: '#0F1629', borderRadius: '14px',
        padding: '28px 24px', maxWidth: '480px', width: '100%',
        border: `1px solid ${theme.surface3}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: theme.text, margin: 0, fontSize: '18px', fontWeight: 700 }}>
            Contact Support
          </h2>
          <button
            onClick={onClose}
            style={{
              background: theme.surface3, border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', color: theme.textMuted,
              cursor: 'pointer', fontSize: '16px', lineHeight: '32px', textAlign: 'center',
            }}
          >✕</button>
        </div>

        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
            <p style={{ color: theme.text, fontWeight: 700, fontSize: '16px', marginBottom: '8px' }}>
              Message sent!
            </p>
            <p style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '24px' }}>
              We'll reply to {userEmail} within 24 hours.
            </p>
            <button
              onClick={onClose}
              style={{
                background: theme.gold, border: 'none', borderRadius: '8px',
                padding: '12px 32px', color: theme.bg,
                fontWeight: 700, fontSize: '15px', cursor: 'pointer',
              }}
            >Done</button>
          </div>
        ) : (
          <>
            <p style={{ color: theme.textMuted, fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
              We'll reply to <strong style={{ color: theme.text }}>{userEmail}</strong>
            </p>

            <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '8px' }}>
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              placeholder="e.g. Workout not saving"
              style={{
                width: '100%', padding: '12px 14px', borderRadius: '8px',
                background: theme.surface1, border: `1px solid ${theme.surface3}`,
                color: theme.text, fontSize: '14px', outline: 'none',
                boxSizing: 'border-box', marginBottom: '16px',
              }}
            />

            <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '8px' }}>
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              placeholder="Describe your issue in as much detail as possible…"
              rows={5}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: '8px',
                background: theme.surface1, border: `1px solid ${theme.surface3}`,
                color: theme.text, fontSize: '14px', outline: 'none', resize: 'vertical',
                boxSizing: 'border-box', marginBottom: '4px', fontFamily: 'inherit',
              }}
            />
            <p style={{ color: theme.textMuted, fontSize: '12px', textAlign: 'right', margin: '0 0 16px' }}>
              {message.length}/2000
            </p>

            {error && (
              <div style={{
                background: '#3B1111', border: `1px solid ${theme.danger}`,
                borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
              }}>
                <p style={{ color: theme.danger, margin: 0, fontSize: '13px' }}>{error}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '13px', borderRadius: '8px',
                  background: theme.surface3, border: 'none',
                  color: theme.text, fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={handleSend}
                disabled={loading || !subject.trim() || !message.trim()}
                style={{
                  flex: 2, padding: '13px', borderRadius: '8px',
                  background: loading || !subject.trim() || !message.trim() ? theme.surface3 : theme.gold,
                  border: 'none',
                  color: loading || !subject.trim() || !message.trim() ? theme.textMuted : theme.bg,
                  fontWeight: 700, fontSize: '14px',
                  cursor: loading || !subject.trim() || !message.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
